import { ChatSessionStatus, type PlaybookRunFragment } from "@qw/graphql";
import { useEffect, useReducer } from "react";

import { compactPlaybookDelayRemainingMs } from "./compact-playbook-mode";

/**
 * Chat-session statuses where the server itself still considers work to be in flight — the
 * client-side mirror of the API's `ACTIVE_CHAT_SESSION_STATUSES`, minus `AWAITING_USER_INPUT`,
 * which means Alby is waiting on the broker and the composer has to stay reachable.
 *
 * This is what keeps a run that nothing is driving from pinning the surface. A `playbook_run` row
 * is not a liveness signal: a worker killed mid-turn by a deploy leaves it `running` indefinitely.
 * The owning session is the signal — `TIMEOUT` is computed at read time from
 * `(status === STREAMING) AND (lastMessageAt > 30min ago)`, and `StaleChatSessionCron` reconciles
 * dead rows every 10 minutes, so an orphaned run stops reading as in-flight without the client
 * having to guess at a staleness window of its own.
 */
const IN_FLIGHT_CHAT_SESSION_STATUSES = new Set<ChatSessionStatus>([
  ChatSessionStatus.Streaming,
  ChatSessionStatus.AwaitingChildPlaybook,
  ChatSessionStatus.Resuming,
]);

export const isChatSessionDrivingPlaybook = (status: ChatSessionStatus | null | undefined): boolean =>
  status !== null && status !== undefined && IN_FLIGHT_CHAT_SESSION_STATUSES.has(status);

/**
 * Returns the active root run's id only when compact mode should apply, and `null` otherwise.
 * Two conditions, composed as AND:
 *
 * 1. **The session is in flight** — the server still considers work to be running. Without this,
 *    a run orphaned by a dead worker pins the surface forever.
 * 2. **The run has aged past the delay** — a short playbook plays out in the normal transcript
 *    instead of bouncing the surface through compact mode and back.
 *
 * The two conditions disagree about an unreadable `startedAt`, and the AND is what resolves it.
 * `compactPlaybookDelayRemainingMs` deliberately falls *toward* compact there, since a run of
 * unknown age may be hours old; the liveness condition falls *open*, to the ordinary chat. Failing
 * open wins, because a missed compaction costs a stall while a wrong compaction costs the composer.
 *
 * A run that is already older than the delay — the reopen case, where a broker returns to a
 * playbook started minutes ago — qualifies on its first render, so reopening a live run never
 * mounts the transcript it is about to tear down.
 */
export const useCompactEligibleRootRunId = (
  rootRun: PlaybookRunFragment | null,
  chatSessionStatus: ChatSessionStatus | null | undefined
): string | null => {
  const [, markDelayElapsed] = useReducer((tick: number) => tick + 1, 0);
  // Gating `startedAt` on liveness keeps the delay timer below from being armed for a run that
  // could never compact anyway.
  const startedAt = rootRun && isChatSessionDrivingPlaybook(chatSessionStatus) ? String(rootRun.startedAt) : null;

  useEffect(() => {
    if (startedAt === null) {
      return;
    }

    // Read the clock here rather than closing over a rendered `now`: the run tree refetches on
    // a subscription and a 30s poll, and a restarted timer measured from a stale `now` would
    // push the flip further out on every refetch.
    const remainingMs = compactPlaybookDelayRemainingMs(startedAt, Date.now());
    if (remainingMs === 0) {
      return;
    }

    const timer = setTimeout(markDelayElapsed, remainingMs);
    return () => clearTimeout(timer);
  }, [startedAt]);

  if (!rootRun || startedAt === null) {
    return null;
  }

  // Checked during render so an already-aged run compacts on its first render. Nothing else
  // forces a render at the moment the delay expires, which is what the timer above covers.
  return compactPlaybookDelayRemainingMs(startedAt, Date.now()) === 0 ? rootRun.id : null;
};
